import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaCheckCircle, FaUpload, FaUser, FaGraduationCap, FaBriefcase, FaFileAlt, FaMapMarkerAlt, FaClock, FaBuilding, FaSearch, FaTimes, FaCode, FaBug, FaHandshake, FaChartLine, FaMobile, FaDatabase } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Button from '@components/ui/Button';

// Countries list
const countries = [
  'Kenya', 'Uganda', 'Tanzania', 'Rwanda', 'Burundi', 'South Sudan', 
  'Ethiopia', 'Somalia', 'Sudan', 'Egypt', 'Nigeria', 'South Africa',
  'Ghana', 'Senegal', 'Morocco', 'Algeria', 'Tunisia', 'Libya',
  'Zambia', 'Zimbabwe', 'Malawi', 'Mozambique', 'Angola', 'Namibia',
  'Botswana', 'Eswatini', 'Lesotho', 'Mauritius', 'Seychelles',
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany',
  'France', 'Netherlands', 'Sweden', 'Norway', 'Denmark', 'Finland',
  'Switzerland', 'Austria', 'Belgium', 'Spain', 'Italy', 'Portugal',
  'Greece', 'Turkey', 'UAE', 'Saudi Arabia', 'Qatar', 'Kuwait',
  'India', 'China', 'Japan', 'South Korea', 'Singapore', 'Malaysia'
];

const educationLevels = [
  'Certificate',
  'Diploma',
  'Bachelor\'s Degree',
  'Master\'s Degree',
  'PhD/Doctorate',
  'Postgraduate Diploma',
  'Professional Certification',
  'Other'
];

const graduationYears = Array.from({ length: 20 }, (_, i) => {
  const year = new Date().getFullYear() - i;
  return year.toString();
});

const departments = [
  { id: 'software-dev', label: 'Software Development', icon: FaCode, color: 'from-blue-500 to-indigo-500' },
  { id: 'qa-testing', label: 'QA Testing', icon: FaBug, color: 'from-emerald-500 to-teal-500' },
  { id: 'erp-consulting', label: 'ERP Consulting', icon: FaHandshake, color: 'from-amber-500 to-orange-500' },
  { id: 'business-analysis', label: 'Business Analysis', icon: FaChartLine, color: 'from-purple-500 to-pink-500' },
  { id: 'mobile-development', label: 'Mobile Development', icon: FaMobile, color: 'from-cyan-500 to-blue-500' },
  { id: 'data-science', label: 'Data Science', icon: FaDatabase, color: 'from-rose-500 to-red-500' },
];

const availableInternships = [
  {
    title: 'Software Development Intern',
    department: 'Engineering',
    location: 'Nairobi, Kenya',
    duration: '3-6 months',
    type: 'Full-time',
    description: 'Work on building cutting-edge financial software solutions using modern technologies.'
  },
  {
    title: 'QA Testing Intern',
    department: 'Quality Assurance',
    location: 'Nairobi, Kenya',
    duration: '3-6 months',
    type: 'Full-time',
    description: 'Learn and apply quality assurance best practices to ensure software reliability.'
  },
  {
    title: 'ERP Consulting Intern',
    department: 'Consulting',
    location: 'Remote',
    duration: '3-6 months',
    type: 'Full-time',
    description: 'Gain hands-on experience in Microsoft Dynamics 365 implementation and consulting.'
  },
  {
    title: 'Business Analysis Intern',
    department: 'Analysis',
    location: 'Nairobi, Kenya',
    duration: '3-6 months',
    type: 'Full-time',
    description: 'Learn to bridge the gap between clients and development teams.'
  },
];

export default function Internship() {
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: '',
    gender: '',
    idNumber: '',
    phone: '',
    email: '',
    country: '',
    education: '',
    institution: '',
    course: '',
    graduationYear: '',
    grade: '',
    department: [],
    availability: '',
    duration: '',
    cv: null,
    transcript: null,
    coverLetter: null,
    declaration: false,
  });

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Validate form
  const validateStep = () => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
      else if (formData.fullName.trim().length < 2) newErrors.fullName = 'Name must be at least 2 characters';

      if (!formData.dateOfBirth) {
        newErrors.dateOfBirth = 'Date of birth is required';
      } else {
        const age = calculateAge(formData.dateOfBirth);
        if (age < 18) newErrors.dateOfBirth = 'You must be at least 18 years old';
        if (age > 65) newErrors.dateOfBirth = 'Age cannot exceed 65 years';
      }

      if (!formData.idNumber.trim()) newErrors.idNumber = 'ID/Passport number is required';
      else if (formData.idNumber.trim().length < 6) newErrors.idNumber = 'ID/Passport must be at least 6 characters';

      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
      else if (!/^\+?[\d\s-]{10,15}$/.test(formData.phone.trim())) {
        newErrors.phone = 'Invalid phone number format (e.g., +254 700 000 000)';
      }

      if (!formData.email.trim()) {
        newErrors.email = 'Email address is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }

      if (!formData.country) newErrors.country = 'Country is required';
    }

    if (step === 2) {
      if (!formData.education) newErrors.education = 'Education level is required';
      if (!formData.institution.trim()) newErrors.institution = 'Institution name is required';
      if (!formData.course.trim()) newErrors.course = 'Course/Program is required';
      if (!formData.graduationYear) newErrors.graduationYear = 'Graduation year is required';
      else if (parseInt(formData.graduationYear) > new Date().getFullYear()) {
        newErrors.graduationYear = 'Graduation year cannot be in the future';
      }
      if (!formData.grade.trim()) newErrors.grade = 'Grade/GPA is required';
    }

    if (step === 3) {
      if (formData.department.length === 0) newErrors.department = 'Select at least one department';
      if (!formData.availability) newErrors.availability = 'Availability is required';
      if (!formData.duration) newErrors.duration = 'Duration is required';
      if (!formData.cv) newErrors.cv = 'CV is required';
      else {
        const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!validTypes.includes(formData.cv.type)) {
          newErrors.cv = 'Please upload a PDF or DOC file';
        }
        if (formData.cv.size > 5 * 1024 * 1024) {
          newErrors.cv = 'File size must be less than 5MB';
        }
      }
      if (!formData.transcript) newErrors.transcript = 'Academic transcript is required';
      else if (!['application/pdf'].includes(formData.transcript.type)) {
        newErrors.transcript = 'Please upload a PDF file';
      }
      if (formData.coverLetter && !['application/pdf'].includes(formData.coverLetter.type)) {
        newErrors.coverLetter = 'Please upload a PDF file for cover letter';
      }
      if (!formData.declaration) newErrors.declaration = 'You must accept the declaration';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files[0]
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateStep()) {
      console.log('Internship application submitted:', formData);
      alert('Application submitted successfully! We will contact you soon.');
    }
  };

  // Filter countries based on search
  const filteredCountries = countries.filter(country =>
    country.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // Click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get age for display
  const age = calculateAge(formData.dateOfBirth);

  // Handle department toggle
  const toggleDepartment = (deptId) => {
    setFormData(prev => ({
      ...prev,
      department: prev.department.includes(deptId)
        ? prev.department.filter(d => d !== deptId)
        : [...prev.department, deptId]
    }));
    if (errors.department) {
      setErrors(prev => ({ ...prev, department: '' }));
    }
  };

  return (
    <div className="py-16 md:py-20 bg-gray-50">
      <div className="container-custom max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <Link to="/careers" className="inline-flex items-center gap-2 text-gray-500 hover:text-micropoint-gold transition-colors text-sm">
            <FaArrowLeft className="text-xs" /> Back to Careers
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <span className="inline-block bg-micropoint-gold/10 text-micropoint-gold px-4 py-1 rounded-full text-sm font-semibold mb-3">
            Apply Now
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 font-display">
            Internship Application <span className="text-micropoint-gold">Program 2026</span>
          </h1>
          <div className="w-20 h-1 bg-micropoint-gold mx-auto mt-4 rounded-full" />
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-sm md:text-base">
            Kickstart your tech career at Micropoint Systems. Fill out the application form below.
          </p>
        </motion.div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Form */}
          <div className="lg:col-span-2">
            {/* Progress Indicator */}
            <div className="flex justify-between items-center mb-8 px-4">
              {[
                { num: 1, label: 'Personal Info', icon: FaUser },
                { num: 2, label: 'Education', icon: FaGraduationCap },
                { num: 3, label: 'Documents', icon: FaFileAlt },
              ].map((item) => (
                <div key={item.num} className="flex flex-col items-center flex-1">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold transition-all ${
                    step >= item.num 
                      ? 'bg-gradient-to-r from-micropoint-gold to-yellow-500 text-white shadow-md' 
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > item.num ? <FaCheckCircle className="text-sm" /> : item.num}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${
                    step >= item.num ? 'text-micropoint-gold' : 'text-gray-400'
                  }`}>
                    {item.label}
                  </span>
                  {item.num < 3 && (
                    <div className={`hidden md:block w-full h-0.5 mt-[-20px] ${
                      step > item.num ? 'bg-micropoint-gold' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200 shadow-sm">
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-xl font-bold text-gray-900 mb-6 font-display flex items-center gap-2">
                    <span className="w-1 h-6 bg-micropoint-gold rounded-full" />
                    Personal Information
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.fullName ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white`}
                      />
                      {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Date of Birth <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.dateOfBirth ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white`}
                      />
                      {age !== null && age >= 18 && (
                        <p className="text-green-600 text-xs mt-1">Age: {age} years (Eligible)</p>
                      )}
                      {age !== null && age < 18 && age > 0 && (
                        <p className="text-red-500 text-xs mt-1">Age: {age} years (Must be at least 18)</p>
                      )}
                      {errors.dateOfBirth && <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Gender</label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white"
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">National ID / Passport <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="idNumber"
                        value={formData.idNumber}
                        onChange={handleChange}
                        placeholder="ID/Passport Number"
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.idNumber ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white`}
                      />
                      {errors.idNumber && <p className="text-red-500 text-xs mt-1">{errors.idNumber}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+254 700 000 000"
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.phone ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white`}
                      />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.email ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white`}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                    <div className="relative" ref={countryRef}>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Country <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search country..."
                          value={countrySearch}
                          onChange={(e) => {
                            setCountrySearch(e.target.value);
                            setShowCountryDropdown(true);
                          }}
                          onFocus={() => setShowCountryDropdown(true)}
                          className="w-full px-4 py-2.5 pl-10 rounded-xl border border-gray-200 focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white"
                        />
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        {countrySearch && (
                          <button
                            onClick={() => {
                              setCountrySearch('');
                              setFormData(prev => ({ ...prev, country: '' }));
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <FaTimes className="text-xs" />
                          </button>
                        )}
                      </div>
                      {showCountryDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                          {filteredCountries.length > 0 ? (
                            filteredCountries.map((country) => (
                              <button
                                key={country}
                                type="button"
                                className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors text-sm ${
                                  formData.country === country ? 'bg-micropoint-gold/10 text-micropoint-gold' : ''
                                }`}
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, country }));
                                  setCountrySearch(country);
                                  setShowCountryDropdown(false);
                                  if (errors.country) {
                                    setErrors(prev => ({ ...prev, country: '' }));
                                  }
                                }}
                              >
                                {country}
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-sm text-gray-500">No countries found</div>
                          )}
                        </div>
                      )}
                      {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-xl font-bold text-gray-900 mb-6 font-display flex items-center gap-2">
                    <span className="w-1 h-6 bg-micropoint-gold rounded-full" />
                    Academic Background
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Highest Level of Education <span className="text-red-500">*</span></label>
                      <select
                        name="education"
                        value={formData.education}
                        onChange={handleChange}
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.education ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white`}
                      >
                        <option value="">Select Level</option>
                        {educationLevels.map(level => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                      {errors.education && <p className="text-red-500 text-xs mt-1">{errors.education}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Institution Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="institution"
                        value={formData.institution}
                        onChange={handleChange}
                        placeholder="University/College Name"
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.institution ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white`}
                      />
                      {errors.institution && <p className="text-red-500 text-xs mt-1">{errors.institution}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Course / Program <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="course"
                        value={formData.course}
                        onChange={handleChange}
                        placeholder="e.g., Computer Science"
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.course ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white`}
                      />
                      {errors.course && <p className="text-red-500 text-xs mt-1">{errors.course}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Year of Graduation <span className="text-red-500">*</span></label>
                      <select
                        name="graduationYear"
                        value={formData.graduationYear}
                        onChange={handleChange}
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.graduationYear ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white`}
                      >
                        <option value="">Select Year</option>
                        {graduationYears.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                      {errors.graduationYear && <p className="text-red-500 text-xs mt-1">{errors.graduationYear}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Current GPA / Grade <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="grade"
                        value={formData.grade}
                        onChange={handleChange}
                        placeholder="e.g., 3.5 or B+"
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.grade ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white`}
                      />
                      {errors.grade && <p className="text-red-500 text-xs mt-1">{errors.grade}</p>}
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-xl font-bold text-gray-900 mb-6 font-display flex items-center gap-2">
                    <span className="w-1 h-6 bg-micropoint-gold rounded-full" />
                    Preferences & Documents
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-3">Preferred Department <span className="text-red-500">*</span></label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {departments.map((dept) => {
                          const Icon = dept.icon;
                          const isSelected = formData.department.includes(dept.id);
                          return (
                            <button
                              key={dept.id}
                              type="button"
                              onClick={() => toggleDepartment(dept.id)}
                              className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                                isSelected
                                  ? `border-micropoint-gold bg-micropoint-gold/5 shadow-md`
                                  : 'border-gray-200 hover:border-micropoint-gold/50 hover:bg-gray-50'
                              }`}
                            >
                              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${dept.color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                                <Icon className="text-white text-sm" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isSelected ? 'text-micropoint-gold' : 'text-gray-700'}`}>
                                  {dept.label}
                                </p>
                              </div>
                              {isSelected && (
                                <FaCheckCircle className="text-micropoint-gold text-sm flex-shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {errors.department && <p className="text-red-500 text-xs mt-2">{errors.department}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Availability <span className="text-red-500">*</span></label>
                      <select
                        name="availability"
                        value={formData.availability}
                        onChange={handleChange}
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.availability ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white`}
                      >
                        <option value="">Select Availability</option>
                        <option value="immediate">Immediate</option>
                        <option value="1month">In 1 Month</option>
                        <option value="3months">In 3 Months</option>
                      </select>
                      {errors.availability && <p className="text-red-500 text-xs mt-1">{errors.availability}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Duration <span className="text-red-500">*</span></label>
                      <select
                        name="duration"
                        value={formData.duration}
                        onChange={handleChange}
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.duration ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white`}
                      >
                        <option value="">Select Duration</option>
                        <option value="3months">3 Months</option>
                        <option value="6months">6 Months</option>
                        <option value="12months">12 Months</option>
                      </select>
                      {errors.duration && <p className="text-red-500 text-xs mt-1">{errors.duration}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">CV / Resume <span className="text-red-500">*</span></label>
                      <input
                        type="file"
                        name="cv"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.cv ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-micropoint-gold/10 file:text-micropoint-gold file:text-sm file:font-semibold hover:file:bg-micropoint-gold/20`}
                      />
                      {formData.cv && (
                        <p className="text-green-600 text-xs mt-1">✓ {formData.cv.name} ({(formData.cv.size / 1024 / 1024).toFixed(2)} MB)</p>
                      )}
                      {errors.cv && <p className="text-red-500 text-xs mt-1">{errors.cv}</p>}
                      <p className="text-xs text-gray-500 mt-1">PDF or DOC format, max 5MB</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Academic Transcript <span className="text-red-500">*</span></label>
                      <input
                        type="file"
                        name="transcript"
                        accept=".pdf"
                        onChange={handleFileChange}
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          errors.transcript ? 'border-red-500' : 'border-gray-200'
                        } focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-micropoint-gold/10 file:text-micropoint-gold file:text-sm file:font-semibold hover:file:bg-micropoint-gold/20`}
                      />
                      {formData.transcript && (
                        <p className="text-green-600 text-xs mt-1">✓ {formData.transcript.name} ({(formData.transcript.size / 1024 / 1024).toFixed(2)} MB)</p>
                      )}
                      {errors.transcript && <p className="text-red-500 text-xs mt-1">{errors.transcript}</p>}
                      <p className="text-xs text-gray-500 mt-1">PDF format only</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Cover Letter <span className="text-gray-400 text-xs">(Optional)</span></label>
                      <input
                        type="file"
                        name="coverLetter"
                        accept=".pdf"
                        onChange={handleFileChange}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-micropoint-gold focus:ring-2 focus:ring-micropoint-gold/20 outline-none transition-all bg-gray-50/50 hover:bg-white file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-micropoint-gold/10 file:text-micropoint-gold file:text-sm file:font-semibold hover:file:bg-micropoint-gold/20"
                      />
                      {formData.coverLetter && (
                        <p className="text-green-600 text-xs mt-1">✓ {formData.coverLetter.name} ({(formData.coverLetter.size / 1024 / 1024).toFixed(2)} MB)</p>
                      )}
                      {errors.coverLetter && <p className="text-red-500 text-xs mt-1">{errors.coverLetter}</p>}
                      <p className="text-xs text-gray-500 mt-1">PDF format (optional)</p>
                    </div>
                    <div className="md:col-span-2 flex items-start gap-3 pt-4 border-t border-gray-200">
                      <input
                        type="checkbox"
                        name="declaration"
                        checked={formData.declaration}
                        onChange={handleChange}
                        className="mt-1 rounded border-gray-300 text-micropoint-gold focus:ring-micropoint-gold w-4 h-4"
                      />
                      <label className="text-sm text-gray-700 leading-relaxed">
                        I confirm that all information provided is accurate and complete. I understand that false information may lead to disqualification. <span className="text-red-500">*</span>
                      </label>
                    </div>
                    {errors.declaration && <p className="text-red-500 text-xs mt-1">{errors.declaration}</p>}
                  </div>
                </motion.div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                <div>
                  {step > 1 && (
                    <button
                      type="button"
                      onClick={handlePrevious}
                      className="px-6 py-2.5 rounded-xl border-2 border-gray-300 text-gray-600 hover:border-micropoint-gold hover:text-micropoint-gold transition-all font-medium text-sm"
                    >
                      ← Previous
                    </button>
                  )}
                </div>
                <div>
                  {step < 3 ? (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="px-8 py-2.5 rounded-xl bg-gradient-to-r from-micropoint-gold to-yellow-500 hover:from-yellow-500 hover:to-micropoint-gold text-white font-semibold transition-all hover:shadow-lg hover:shadow-micropoint-gold/20 text-sm"
                    >
                      Next Step →
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="px-8 py-2.5 rounded-xl bg-gradient-to-r from-micropoint-gold to-yellow-500 hover:from-yellow-500 hover:to-micropoint-gold text-white font-semibold transition-all hover:shadow-lg hover:shadow-micropoint-gold/20 text-sm flex items-center gap-2"
                    >
                      Submit Application <FaCheckCircle className="text-sm" />
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Right Column - Available Internships */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <FaBriefcase className="text-micropoint-gold text-xl" />
                  <h2 className="text-xl font-bold text-gray-900 font-display">Available Internships</h2>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Explore the internship opportunities available at Micropoint Systems.
                </p>

                <div className="space-y-4">
                  {availableInternships.map((internship, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="p-4 rounded-xl border border-gray-200 hover:border-micropoint-gold transition-all hover:shadow-md group"
                    >
                      <h4 className="font-bold text-gray-900 text-sm group-hover:text-micropoint-gold transition-colors">
                        {internship.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <FaBuilding className="text-[10px]" />
                        <span>{internship.department}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <FaMapMarkerAlt className="text-[10px]" /> {internship.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <FaClock className="text-[10px]" /> {internship.duration}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-2 leading-relaxed">
                        {internship.description}
                      </p>
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <span className="inline-block bg-micropoint-gold/10 text-micropoint-gold text-[10px] px-2 py-0.5 rounded-full">
                          {internship.type}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="bg-gradient-to-r from-micropoint-blue to-blue-700 rounded-xl p-4 text-center text-white">
                    <p className="text-sm font-medium">📢 New positions added regularly</p>
                    <p className="text-xs text-white/70 mt-1">Check back often for updates</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-8 text-center text-sm text-gray-500"
        >
          <p>
            For any questions regarding the internship program, please contact us at{' '}
            <a href="mailto:careers@micropoint.co.ke" className="text-micropoint-gold hover:text-yellow-600 transition-colors">
              careers@micropoint.co.ke
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}